import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Space, Tag, Tooltip } from "antd";
import { PlayCircleOutlined, FileDoneOutlined, ReloadOutlined, UserOutlined } from "@ant-design/icons";
import axios from "axios";
import Cookies from "universal-cookie";
import { toast } from "react-toastify";
import SurveyWizardModal from "./SurveyWizardModal";
import ClosingQuestionnaireModal from "./ClosingQuestionnaireModal";
import ClosingQuestionnaireReadOnly from "./ClosingQuestionnaireReadOnly";

const normalizeTasksPayload = (payload) => {
  const d = payload?.data ?? payload;
  if (Array.isArray(d?.tasks)) return d.tasks;
  const inc = Array.isArray(d?.taskInCompleted) ? d.taskInCompleted : [];
  const comp = Array.isArray(d?.taskCompleted) ? d.taskCompleted : [];
  if (inc.length || comp.length) return [...inc, ...comp];
  if (Array.isArray(payload)) return payload;
  return [];
};

export default function AppointmentActionsLite({
  tranId,
  adminOverrideAllowed = false,
}) {
  const cookies = new Cookies();
  const token = cookies.get("token");
  const authHeaders = { Authorization: `Bearer ${token}` };

  const [wizardOpen, setWizardOpen] = useState(false);
  const [closingOpen, setClosingOpen] = useState(false);
  const [loadingCount, setLoadingCount] = useState(false);
  const [incompleteCount, setIncompleteCount] = useState(null);
  const [totalSeen, setTotalSeen] = useState(null);

  // Work order state
  const [reconStatus, setReconStatus] = useState("");
  const [assignedTechId, setAssignedTechId] = useState("");
  const [assignedTechName, setAssignedTechName] = useState("");

  // Current user tech (from cookie or localStorage)
  const [currentTechId, setCurrentTechId] = useState("");
  const [currentTechName, setCurrentTechName] = useState("");

  // ----- who is the current user (tech) -----
  useEffect(() => {
    const cookieTechId = cookies.get("techId") || cookies.get("userTechId");
    const cookieName = cookies.get("name") || cookies.get("userName");
    let lsTechId = "", lsName = "";
    try {
      const raw = localStorage.getItem("userData");
      if (raw) {
        const u = JSON.parse(raw);
        lsTechId = u?.user?.techId || u?.techId || "";
        lsName = u?.user?.name || u?.name || "";
      }
    } catch { }
    setCurrentTechId(String(cookieTechId || lsTechId || "").trim());
    setCurrentTechName(String(cookieName || lsName || "").trim());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----- fetch work order (status + assignment) -----
  const fetchWorkOrder = useCallback(async () => {
    if (!tranId) return;
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API}/workOrders/tranId/${tranId}`,
        { headers: authHeaders }
      );
      const wo = res?.data?.workOrder || res?.data?.data || res?.data || {};
      setReconStatus(wo?.reconciliationStatus || "");
      setAssignedTechId(String(wo?.techId || wo?.tech_id || "").trim());
      setAssignedTechName(String(wo?.techName || wo?.tech_name || "").trim());
    } catch {
      setReconStatus("");
      setAssignedTechId("");
      setAssignedTechName("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tranId]);

  // ----- fetch ALL tasks (paginated) and count incomplete client-side -----
  const fetchIncompleteCount = useCallback(async () => {
    if (!tranId) return;
    setLoadingCount(true);
    try {
      let url = `${process.env.REACT_APP_API}/task/task-by-tran-id/${tranId}?perPage=50&page=1`;
      let remaining = 0;
      let total = 0;
      const seen = new Set();

      while (url) {
        const res = await axios.get(url, { headers: authHeaders });
        const data = res.data || {};
        const list = normalizeTasksPayload(data);
        total = data.totalTasks ?? total;

        for (const t of list) {
          const id = t?._id || t?.id || t?.taskId;
          if (!id || seen.has(id)) continue;
          seen.add(id);
          if (t?.isCompleted === false) remaining++;
        }

        url = data?.next_page_url || null;
      }

      setTotalSeen(total || seen.size);
      setIncompleteCount(remaining);
    } catch (e) {
      setIncompleteCount(null);
      toast.error("Unable to fetch tasks.");
    } finally {
      setLoadingCount(false);
    }
  }, [tranId]);

  useEffect(() => {
    fetchWorkOrder();
    fetchIncompleteCount();
  }, [fetchWorkOrder, fetchIncompleteCount]);

  const onWizardClosed = () => {
    setWizardOpen(false);
    fetchIncompleteCount();
  };

  const onWizardAllDone = () => {
    setIncompleteCount(0);
    toast.success("All tasks completed. You can now submit the Closing Questionnaire.");
  };

  const isCompletedWO = String(reconStatus).toLowerCase() === "completed";

  // Gate: only assigned tech can act
  const isAssigned = useMemo(() => {
    if (!assignedTechId || !currentTechId) return false;
    return String(assignedTechId) === String(currentTechId);
  }, [assignedTechId, currentTechId]);

  // ----- COMPLETED: read-only view for everyone -----
  if (isCompletedWO) {
    return (
      <>
        <Space wrap size="small" style={{ marginBottom: 8 }}>
          <Tag color="green">Work order completed</Tag>
          {assignedTechId && (
            <Tag icon={<UserOutlined />} color="green">
              Assigned to: {assignedTechName || assignedTechId}
            </Tag>
          )}
          {typeof totalSeen === "number" && (
            <Tag color="green">Tasks: {totalSeen} / {totalSeen}</Tag>
          )}
        </Space>
        <ClosingQuestionnaireReadOnly tranId={tranId} />
      </>
    );
  }

  // ----- ACTIVE: show actions only if assigned -----
  return (
    <>
      <Space wrap size="small" style={{ marginBottom: 8 }}>
        {assignedTechId && (
          <Tag icon={<UserOutlined />} color={isAssigned ? "blue" : "gold"}>
            Assigned to: {assignedTechName || assignedTechId}
          </Tag>
        )}

        {!isAssigned && (
          <Tag color="gold">You are not the assigned technician</Tag>
        )}

        {typeof totalSeen === "number" && (
          <Tag color={incompleteCount === 0 ? "green" : "blue"}>
            {incompleteCount === 0
              ? "All tasks complete"
              : `${incompleteCount ?? "?"} remaining`} {typeof totalSeen === "number" ? `/ ${totalSeen}` : ""}
          </Tag>
        )}

        {/* Refresh is harmless for any viewer */}
        <Tooltip title="Refresh task status">
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchIncompleteCount}
            loading={loadingCount}
          >
            Refresh
          </Button>
        </Tooltip>

        {/* Actions ONLY for assigned tech */}
        {isAssigned && (
          <>
            <Tooltip title="Walk through the survey tasks">
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                onClick={() => setWizardOpen(true)}
              >
                Start Job
              </Button>
            </Tooltip>

            {incompleteCount === 0 && (
              <Tooltip title="All tasks are complete. Submit final answers.">
                <Button
                  type="primary"
                  danger
                  icon={<FileDoneOutlined />}
                  onClick={() => setClosingOpen(true)}
                >
                  Closing Questionnaire
                </Button>
              </Tooltip>
            )}
          </>
        )}
      </Space>

      {/* Survey wizard (only openable if assigned) */}
      <SurveyWizardModal
        open={wizardOpen}
        onClose={onWizardClosed}
        onAllDone={onWizardAllDone}
        tranId={tranId}
        adminOverrideAllowed={adminOverrideAllowed}
      />

      {/* Closing questionnaire (manual trigger, only for assigned) */}
      <ClosingQuestionnaireModal
        open={closingOpen}
        onClose={() => setClosingOpen(false)}
        tranId={tranId}
        onSubmitted={() => {
          setClosingOpen(false);
          fetchIncompleteCount();
          fetchWorkOrder();
        }}
      />
    </>
  );
}

